import type React from 'react';
import { ComboBox, Input, ListBox } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { AccountOption } from '@/pages/accounting/journalEntry/journalEntryMeta';

type AssetAccountPickerProps = {
    ariaLabel: string;
    value: string | null;
    options: AccountOption[];
    placeholder: string;
    isInvalid?: boolean;
    onChange: (accountId: string | null) => void;
    /** testid discriminator appended after the literal slot. */
    slot: string;
};

/**
 * Searchable postable-account picker — the same React-Aria `ComboBox` the
 * journal's line picker uses, with the same `"code name"` search text so typing
 * either the code (`1120`) or part of the localized name matches in either
 * locale. Used for the acquisition's funding account and the disposal's proceeds
 * account.
 *
 * `getNodeTestIdProp` (data-testid only, no `id`) is deliberate — an explicit
 * `id` on the RAC ComboBox root triggers a setState loop.
 */
export const AssetAccountPicker: React.FC<AssetAccountPickerProps> = ({
    ariaLabel,
    value,
    options,
    placeholder,
    isInvalid,
    onChange,
    slot,
}) => (
    <ComboBox<AccountOption>
        aria-label={ariaLabel}
        defaultItems={options}
        isInvalid={isInvalid}
        menuTrigger="focus"
        onSelectionChange={(key) => onChange(key === null ? null : String(key))}
        selectedKey={value}
        {...getNodeTestIdProp(`fixed-assets-account-picker-${slot}`)}
    >
        <ComboBox.InputGroup>
            <Input placeholder={placeholder} />
            <ComboBox.Trigger />
        </ComboBox.InputGroup>
        <ComboBox.Popover>
            <ListBox>
                {(item: AccountOption) => (
                    <ListBox.Item id={item.id} textValue={item.searchText}>
                        <span className="flex min-w-0 items-center gap-2">
                            <span className="shrink-0 text-xs tabular-nums text-muted">
                                {item.code}
                            </span>
                            <span className="truncate">{item.name}</span>
                        </span>
                    </ListBox.Item>
                )}
            </ListBox>
        </ComboBox.Popover>
    </ComboBox>
);
